import api from '../axios'
import type { DashboardAlertsResponse, PendingVotesResponse } from '@/interface/dashboard'

// Alerts Management API types (scoped locally to keep changes minimal)
export type AlertMetricType = 'APV' | 'TRAFFIC_SOURCE'
export type AlertOperator = 'GREATER_THAN' | 'LESS_THAN'

export interface SetYouTubeUrlDto {
  youtubeUrl: string
}

export interface AlertVideoResponseDto {
  id: string
  finalEditId: string
  youtubeVideoId: string
  youtubeUrl: string
  isValid: boolean
  videoPublishedAt: string
  createdAt: string
  updatedAt: string
}

export interface CreateAlertDto {
  metricType: AlertMetricType
  operator: AlertOperator
  threshold: number
}

export interface UpdateAlertDto {
  metricType?: AlertMetricType
  operator?: AlertOperator
  threshold?: number
}

export interface AlertResponseDto {
  id: string
  finalEditId: string
  metricType: AlertMetricType
  operator: AlertOperator
  threshold: number
  isActive: boolean
  lastCheckedAt?: string
  lastTriggeredAt?: string
  createdAt: string
  updatedAt: string
}

export interface AlertListResponseDto {
  alerts: AlertResponseDto[]
  totalAlerts: number
  activeAlerts: number
  youtubeUrl?: string
}

export interface AlertActivationResponseDto {
  success: boolean
  message: string
  isActive: boolean
  alertId: string
}

export const alertsApi = {
  async setYouTubeUrl(projectId: string, finalEditId: string, data: SetYouTubeUrlDto): Promise<AlertVideoResponseDto> {
    const res = await api.post<AlertVideoResponseDto>(`/projects/${projectId}/final-edits/${finalEditId}/youtube`, data)
    return res.data
  },

  async getYouTubeUrl(projectId: string, finalEditId: string): Promise<AlertVideoResponseDto> {
    const res = await api.get<AlertVideoResponseDto>(`/projects/${projectId}/final-edits/${finalEditId}/youtube`)
    return res.data
  },

  async updateYouTubeUrl(projectId: string, finalEditId: string, data: SetYouTubeUrlDto): Promise<AlertVideoResponseDto> {
    const res = await api.put<AlertVideoResponseDto>(`/projects/${projectId}/final-edits/${finalEditId}/youtube`, data)
    return res.data
  },

  async createAlert(projectId: string, finalEditId: string, dto: CreateAlertDto): Promise<AlertResponseDto> {
    const res = await api.post<AlertResponseDto>(`/projects/${projectId}/final-edits/${finalEditId}/youtube/alerts`, dto)
    return res.data
  },

  async getAlerts(projectId: string, finalEditId: string): Promise<AlertListResponseDto> {
    const res = await api.get<AlertListResponseDto>(`/projects/${projectId}/final-edits/${finalEditId}/youtube/alerts`)
    return res.data
  },

  async getAlert(projectId: string, finalEditId: string, alertId: string): Promise<AlertResponseDto> {
    const res = await api.get<AlertResponseDto>(
      `/projects/${projectId}/final-edits/${finalEditId}/youtube/alerts/${alertId}`,
    )
    return res.data
  },

  async updateAlert(
    projectId: string,
    finalEditId: string,
    alertId: string,
    dto: UpdateAlertDto,
  ): Promise<AlertResponseDto> {
    const res = await api.put<AlertResponseDto>(
      `/projects/${projectId}/final-edits/${finalEditId}/youtube/alerts/${alertId}`,
      dto,
    )
    return res.data
  },

  async deleteAlert(projectId: string, finalEditId: string, alertId: string): Promise<AlertResponseDto> {
    const res = await api.delete<AlertResponseDto>(
      `/projects/${projectId}/final-edits/${finalEditId}/youtube/alerts/${alertId}`,
    )
    return res.data
  },

  async activateAlert(projectId: string, finalEditId: string, alertId: string): Promise<AlertActivationResponseDto> {
    const res = await api.patch<AlertActivationResponseDto>(
      `/projects/${projectId}/final-edits/${finalEditId}/alerts/${alertId}/activate`,
    )
    return res.data
  },

  async deactivateAlert(projectId: string, finalEditId: string, alertId: string): Promise<AlertActivationResponseDto> {
    const res = await api.patch<AlertActivationResponseDto>(
      `/projects/${projectId}/final-edits/${finalEditId}/alerts/${alertId}/deactivate`,
    )
    return res.data
  },

  // Dashboard alerts
  async getDashboardAlerts(): Promise<DashboardAlertsResponse> {
    const res = await api.get<DashboardAlertsResponse>('/dashboard/alerts')
    return res.data
  },

  // Dashboard pending votes
  async getPendingVotes(): Promise<PendingVotesResponse> {
    const res = await api.get<PendingVotesResponse>('/dashboard/pending-votes')
    return res.data
  },
}
